import { Link } from "react-router-dom";
import { Microscope, HeartHandshake, GraduationCap, FileText, Check, ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const SERVICES = [
  {
    icon: Microscope,
    title: "STEM Tutoring",
    tagline: "All subjects. All levels. Taught by someone who actually uses the material.",
    bullets: [
      "Biology, Chemistry, Organic Chemistry, Biochemistry",
      "Physics, Calculus, Statistics",
      "Anatomy, Physiology, Microbiology, Genetics, Pharmacology",
      "Retrieval-based study plans & mock exams",
    ],
    accent: "forest",
  },
  {
    icon: HeartHandshake,
    title: "MMI Coaching",
    tagline: "Station-by-station practice. Honest feedback. A framework that works under pressure.",
    bullets: [
      "Ethics, collaborative, acting & traditional stations",
      "3-beat response structure + live drills",
      "Video review of your body language & pacing",
      "Mock circuit under real interview conditions",
    ],
    accent: "terracotta",
  },
  {
    icon: GraduationCap,
    title: "Academic & Admissions Coaching",
    tagline: "Maps, timelines, school lists. The hidden curriculum, made visible.",
    bullets: [
      "Application timeline planning",
      "School list strategy (reach / match / safety)",
      "Reference letter guidance",
      "Interview prep beyond the MMI",
    ],
    accent: "scholar",
  },
  {
    icon: FileText,
    title: "Essay Preparation",
    tagline: "From blank page to a personal statement that sounds like you, not a template.",
    bullets: [
      "Brainstorming & narrative architecture",
      "Line-by-line editing with voice preservation",
      "Secondary & diversity essays",
      "No ghostwriting, ever. Your voice leads",
    ],
    accent: "ink",
  },
];

const FAQ = [
  {
    q: "Do you work with high-school students or only university?",
    a: "Both. I tutor high-school STEM up through university-level courses and professional admissions.",
  },
  {
    q: "Do you work with neurodivergent students (ADHD, autism, dyslexia)?",
    a: "Yes, and it's some of my favorite work. Many of my students are neurodivergent (as am I), and I build study plans that work with the way your brain actually learns: flexible pacing, retrieval-based practice, and clear scaffolding instead of unstructured memorization. I won't pretend to be a learning specialist or therapist, but I'll meet you where you are and adjust as we go.",
  },
  {
    q: "How are sessions delivered?",
    a: "1:1 over Zoom. You'll get a shared doc, a whiteboard, and a replay link if you want to revisit.",
  },
  {
    q: "What's your pricing?",
    a: "It depends on the service and package. Reach out via the contact form and I'll send a clear rate card within a day.",
  },
  {
    q: "Do you guarantee admission?",
    a: "No one honest does. What I guarantee is a clear plan, attentive coaching, and a stronger application than the one you started with.",
  },
  {
    q: "Do you use AI to write essays?",
    a: "Absolutely not. AI flattens your voice, which is the one thing adcoms actually remember. I coach you through your own draft.",
  },
  {
    q: "How long does coaching typically last?",
    a: "MMI prep is usually 4 to 6 weeks. STEM tutoring is ongoing by semester. Essays are package-based (3 to 5 rounds of edits).",
  },
];

const ACCENT_MAP = {
  forest: "bg-forest text-paper-50",
  terracotta: "bg-terracotta text-paper-50",
  scholar: "bg-scholar text-ink",
  ink: "bg-ink text-paper-50",
};

export default function Services() {
  return (
    <div data-testid="services-page">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 lg:pt-24 pb-8">
        <p className="overline text-terracotta">Services</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-ink mt-4 max-w-4xl">
          Pick what you need. Skip what you don't. Pay for nothing extra.
        </h1>
        <p className="mt-6 text-lg text-ink-muted max-w-2xl leading-relaxed">
          Every student arrives at a different place on the map. Mix and match the services below,
          or start with a free intro call and we'll figure it out together.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12 space-y-5">
        {SERVICES.map((s, i) => (
          <article
            key={s.title}
            data-testid={`service-card-${i}`}
            className="grid grid-cols-1 lg:grid-cols-12 gap-6 bg-paper-100 border border-paper-200 p-7 lg:p-10 rounded-sm hover:border-forest transition-colors"
          >
            <div className="lg:col-span-4">
              <div className={`inline-flex items-center justify-center w-14 h-14 rounded-sm ${ACCENT_MAP[s.accent]}`}>
                <s.icon className="w-7 h-7" strokeWidth={1.8} />
              </div>
              <h2 className="font-display font-bold text-2xl lg:text-3xl tracking-tight text-ink mt-5 leading-tight">
                {s.title}
              </h2>
              <p className="mt-3 text-ink-muted leading-relaxed">{s.tagline}</p>
            </div>
            <div className="lg:col-span-8 lg:border-l lg:border-paper-200 lg:pl-10">
              <ul className="space-y-3">
                {s.bullets.map((b, k) => (
                  <li key={k} className="flex items-start gap-3 text-ink">
                    <Check className="w-5 h-5 text-forest shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                data-testid={`service-cta-${i}`}
                className="mt-7 inline-flex items-center gap-2 text-forest font-semibold link-underline"
              >
                Inquire about {s.title.toLowerCase()} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </article>
        ))}
      </section>

      {/* FAQ */}
      <section className="bg-paper-100 border-y border-paper-200">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-20">
          <p className="overline text-terracotta">Frequently asked</p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-ink mt-3 mb-10">
            The questions students ask in the intro call.
          </h2>
          <Accordion type="single" collapsible className="w-full" data-testid="services-faq">
            {FAQ.map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-paper-200">
                <AccordionTrigger
                  data-testid={`faq-trigger-${i}`}
                  className="font-display font-bold text-lg lg:text-xl tracking-tight text-ink hover:text-forest text-left"
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-ink-muted leading-relaxed text-base">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-20 text-center">
        <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight leading-tight text-ink">
          Still not sure which service fits?
        </h2>
        <p className="mt-4 text-ink-muted max-w-xl mx-auto">
          That's exactly what the free intro call is for. Twenty minutes to talk through where you are, where you want to go, and find the right next step together.
        </p>
        <Link
          to="/contact"
          data-testid="services-bottom-cta"
          className="mt-8 inline-flex items-center gap-2 bg-terracotta text-paper-50 px-7 py-4 text-base font-semibold rounded-sm hover:bg-terracotta-dark hover:-translate-y-0.5 transition-all"
        >
          Book your intro call <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </div>
  );
}
